import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="sector-products"
export default class extends Controller {
  static targets = [
    "productInput",
    "addButton",
    "toggleButton",
    "toggleIcon",
    "toggleText",
    "commonProducts",
    "selectedProducts",
    "productIdsField",
    "searchDropdown",
    "searchStatus",
    "searchResults",
  ];

  get sectorId() {
    const sectorSelect =
      document.querySelector('[data-facility-wizard-target="sectorSelect"]') ||
      document.querySelector('[data-facility-edit-target="sectorSelect"]');

    // For edit page, we might need to get sector from a data attribute or hidden field
    let sectorId = sectorSelect ? sectorSelect.value : "";

    // If no sector select found, try to get from a data attribute on the form
    const form = this.element.closest("form");
    if (form) {
      if (!sectorId) {
        sectorId = form.dataset.sectorId;
      }
    }
    return sectorId;
  }

  connect() {
    this.selectedProductIds = new Set();
    this.commonProductsVisible = false;
    this.allProducts = []; // Store all available products for search
    this.searchTimeout = null;
    this.productsLoaded = false; // Track if products have been loaded

    // Initialize with existing products if any
    this.initializeExistingProducts();

    // Load common products and then update display
    this.loadCommonProducts();

    // Initial validation with delay to ensure DOM is ready
    setTimeout(() => {
      this.validateForm();
    }, 100);

    // Setup click outside handler for search dropdown
    this.handleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener("click", this.handleClickOutside);
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside);
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  initializeExistingProducts() {
    const existingIds = this.productIdsFieldTarget.value;
    if (existingIds) {
      const ids = existingIds.split(",").filter((id) => id.trim());
      ids.forEach((id) => this.selectedProductIds.add(id.trim()));
      // Don't update display immediately - wait for products to load
      // this.updateSelectedProductsDisplay();
    }
  }

  sectorChanged() {
    // Reload common products when sector changes
    this.loadCommonProducts();
    // Also trigger validation with a delay to ensure DOM is updated
    setTimeout(() => {
      this.validateForm();
    }, 50);
  }

  unitChanged() {
    // Trigger validation when unit changes
    setTimeout(() => {
      this.validateForm();
    }, 50);
  }

  handleProductKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.addProduct();
    }
  }

  validateProductInput() {
    const productName = this.productInputTarget.value.trim();
    this.addButtonTarget.disabled = !productName;

    // Trigger search with debounce
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      this.performSearch(productName);
    }, 300);
  }

  // Method called by template
  searchProducts() {
    this.validateProductInput();
  }

  // Method called by template
  showDropdown() {
    const query = this.productInputTarget.value.trim();
    this.performSearch(query);
  }

  // Method called by template
  hideDropdown() {
    // Add a small delay to allow clicks on dropdown items
    setTimeout(() => {
      this.hideSearchDropdown();
    }, 150);
  }

  // Method called by template
  addSelectedProduct() {
    this.addProduct();
  }

  performSearch(query) {
    let filteredProducts = this.allProducts;

    // Filter products based on query
    if (query) {
      filteredProducts = this.allProducts.filter(
        (product) =>
          product.name.toLowerCase().includes(query.toLowerCase()) &&
          !this.selectedProductIds.has(product.id.toString())
      );
    }

    this.showSearchDropdown(filteredProducts, query);
  }

  showSearchDropdown(products, query) {
    if (!this.hasSearchDropdownTarget) return;

    // Update status
    if (this.hasSearchStatusTarget) {
      if (products.length === 0) {
        if (!query) {
          this.searchStatusTarget.textContent = `No products found.`;
        } else {
          this.searchStatusTarget.textContent = `No products found. Press Enter to add "${query}" as a new product.`;
        }
      } else {
        this.searchStatusTarget.textContent = `Found ${
          products.length
        } product${products.length === 1 ? "" : "s"}`;
      }
    }

    // Update results
    if (this.hasSearchResultsTarget) {
      if (products.length === 0) {
        this.searchResultsTarget.innerHTML = "";
      } else {
        const html = products
          .map(
            (product) => `
          <button type="button"
                  class="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0"
                  data-product-id="${product.id}"
                  data-product-name="${product.name}"
                  data-action="click->sector-products#selectSearchProduct">
            ${this.highlightMatch(product.name, query)}
          </button>
        `
          )
          .join("");

        this.searchResultsTarget.innerHTML = html;
      }
    }

    this.searchDropdownTarget.classList.remove("hidden");
  }

  highlightMatch(text, query) {
    const regex = new RegExp(`(${query})`, "gi");
    return text.replace(regex, "<strong>$1</strong>");
  }

  hideSearchDropdown() {
    if (this.hasSearchDropdownTarget) {
      this.searchDropdownTarget.classList.add("hidden");
    }
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideSearchDropdown();
    }
  }

  selectSearchProduct(event) {
    // Use currentTarget to get the button element (event.target may be a child element like <strong>)
    const button = event.currentTarget;
    const productId = button.dataset.productId;
    const productName = button.dataset.productName;

    if (!productId) return;

    if (!this.selectedProductIds.has(productId)) {
      this.selectedProductIds.add(productId);
      this.updateSelectedProductsDisplay();
      this.updateHiddenField();
      this.validateForm();
    }

    // Clear input and hide dropdown
    this.productInputTarget.value = "";
    this.addButtonTarget.disabled = true;
    this.hideSearchDropdown();
  }

  addProduct() {
    const productName = this.productInputTarget.value.trim();
    if (!productName) return;

    // Check if product already exists in our list
    const existingProduct = this.findProductByName(productName);
    if (existingProduct) {
      this.selectedProductIds.add(existingProduct.id.toString());
    } else {
      // For new products, we'll use a temporary ID with the name
      // The backend will handle creating new products
      const tempId = `new_${Date.now()}_${productName.replace(/\s+/g, "_")}`;
      this.selectedProductIds.add(tempId);
    }

    // Clear input and update display
    this.productInputTarget.value = "";
    this.addButtonTarget.disabled = true;
    this.updateSelectedProductsDisplay();
    this.updateHiddenField();

    // Add a delay before validation to ensure hidden field is updated
    setTimeout(() => {
      this.validateForm();
    }, 50);
  }

  findProductByName(name) {
    // This would need to search through available products only
    // For now  we want to be able to create a new products
    return null;
  }

  toggleCommonProducts() {
    this.commonProductsVisible = !this.commonProductsVisible;

    if (!this.hasCommonProductsTarget) return;

    if (this.commonProductsVisible) {
      this.commonProductsTarget.classList.remove("hidden");
      this.toggleIconTarget.classList.add("rotate-180");
      this.toggleTextTarget.textContent = "Hide common products";
    } else {
      this.commonProductsTarget.classList.add("hidden");
      this.toggleIconTarget.classList.remove("rotate-180");
      this.toggleTextTarget.textContent = "Show common products";
    }
  }

  loadCommonProducts() {
    // Get the selected sector - try both wizard and edit contexts
    const sectorSelect =
      document.querySelector('[data-facility-wizard-target="sectorSelect"]') ||
      document.querySelector('[data-facility-edit-target="sectorSelect"]');

    // For edit page, we might need to get sector from a data attribute or hidden field
    let sectorId = sectorSelect ? sectorSelect.value : "";
    let facilityId = null;

    // If no sector select found, try to get from a data attribute on the form
    const form = this.element.closest("form");
    if (form) {
      if (!sectorId) {
        sectorId = form.dataset.sectorId;
      }
      facilityId = form.dataset.facilityId;
    }

    if (!sectorId) {
      // Even without sector, try to load facility products if we have a facilityId
      if (facilityId) {
        this.loadFacilityProducts(facilityId);
        return;
      }

      if (this.hasCommonProductsTarget) {
        this.commonProductsTarget.innerHTML =
          '<p class="text-sm text-gray-500">Select a sector to see common products</p>';
      }
      this.allProducts = [];
      this.productsLoaded = true; // Mark as loaded even if no sector
      // Still update selected products display in case we have selected products
      this.updateSelectedProductsDisplay();
      // Trigger validation even when no sector
      this.validateForm();
      return;
    }

    // Fetch common products for the selected sector
    const currentLocale = window.location.pathname.split("/")[1];
    const url = `/${currentLocale}/api/sectors/${sectorId}/products`;

    fetch(url, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const sectorProducts = data.products || [];

        // If we have a facilityId, also load facility products and merge them
        if (facilityId) {
          this.loadAndMergeFacilityProducts(facilityId, sectorProducts);
        } else {
          this.allProducts = sectorProducts;
          this.productsLoaded = true;
          this.renderCommonProducts(sectorProducts);
          this.updateSelectedProductsDisplay();
          this.validateForm();
        }
      })
      .catch((error) => {
        if (
          (error.message.includes("401") ||
            error.message.includes("You need to sign in")) &&
          this.hasCommonProductsTarget
        ) {
          this.commonProductsTarget.innerHTML =
            '<p class="text-sm text-gray-500">Please sign in to load products</p>';
        } else {
          this.commonProductsTarget.innerHTML =
            '<p class="text-sm text-gray-500">Error loading products</p>';
        }
        this.allProducts = [];
        this.productsLoaded = true; // Mark as loaded even on error
        // Still update selected products display in case we have selected products
        this.updateSelectedProductsDisplay();
        // Trigger validation even on error
        this.validateForm();
      });
  }

  loadFacilityProducts(facilityId) {
    const currentLocale = window.location.pathname.split("/")[1];
    const url = `/${currentLocale}/api/facilities/${facilityId}/products`;

    fetch(url, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const products = data.products || [];
        this.allProducts = products;
        this.productsLoaded = true;
        this.renderCommonProducts(products);
        this.updateSelectedProductsDisplay();
        this.validateForm();
      })
      .catch((error) => {
        console.error("Error loading facility products:", error);
        this.allProducts = [];
        this.productsLoaded = true;
        this.updateSelectedProductsDisplay();
        this.validateForm();
      });
  }

  loadAndMergeFacilityProducts(facilityId, sectorProducts) {
    const currentLocale = window.location.pathname.split("/")[1];
    const url = `/${currentLocale}/api/facilities/${facilityId}/products`;

    fetch(url, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const facilityProducts = data.products || [];

        // Merge products, ensuring no duplicates
        const productMap = new Map();
        sectorProducts.forEach((p) => productMap.set(p.id.toString(), p));
        facilityProducts.forEach((p) => productMap.set(p.id.toString(), p));

        this.allProducts = Array.from(productMap.values());
        this.productsLoaded = true;
        this.renderCommonProducts(sectorProducts);
        this.updateSelectedProductsDisplay();
        this.validateForm();
      })
      .catch((error) => {
        console.error("Error loading facility products:", error);
        // Fall back to just sector products
        this.allProducts = sectorProducts;
        this.productsLoaded = true;
        this.renderCommonProducts(sectorProducts);
        this.updateSelectedProductsDisplay();
        this.validateForm();
      });
  }

  renderCommonProducts(products) {
    if (!this.hasCommonProductsTarget) return;

    if (products.length === 0) {
      this.commonProductsTarget.innerHTML =
        '<p class="text-sm text-gray-500">No common products available</p>';
      return;
    }

    const html = products
      .map(
        (product) => `
      <button type="button"
              class="text-left text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 px-2 py-1 rounded"
              data-product-id="${product.id}"
              data-product-name="${product.name}"
              data-action="click->sector-products#selectCommonProduct">
        ${product.name}
      </button>
    `
      )
      .join("");

    this.commonProductsTarget.innerHTML = html;
  }

  selectCommonProduct(event) {
    const productId = event.target.dataset.productId;
    const productName = event.target.dataset.productName;

    if (!this.selectedProductIds.has(productId)) {
      this.selectedProductIds.add(productId);
      this.updateSelectedProductsDisplay();
      this.updateHiddenField();

      // Add a delay before validation to ensure hidden field is updated
      setTimeout(() => {
        this.validateForm();
      }, 50);
    }
  }

  removeProduct(event) {
    event.preventDefault();
    event.stopPropagation();

    // Find the button element (might be clicked on SVG or path inside)
    const button = event.target.closest("[data-product-id]");
    if (!button) return;

    const productId = button.dataset.productId;

    this.selectedProductIds.delete(productId);
    this.updateSelectedProductsDisplay();
    this.updateHiddenField();
    this.validateForm();
  }

  updateSelectedProductsDisplay() {
    if (this.selectedProductIds.size === 0) {
      this.selectedProductsTarget.innerHTML = "";
      return;
    }

    const html = Array.from(this.selectedProductIds)
      .map((productId) => {
        const productName = this.getProductName(productId);
        return `
        <span class="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 mr-2 mb-2">
          ${productName}
          <button type="button"
                  class="ml-2 text-gray-400 hover:text-gray-600"
                  data-product-id="${productId}"
                  data-action="click->sector-products#removeProduct">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </span>
      `;
      })
      .join("");

    this.selectedProductsTarget.innerHTML = html;
  }

  getProductName(productId) {
    // If it's a temporary ID (new product), extract the name
    if (productId.startsWith("new_")) {
      const parts = productId.split("_");
      return parts.slice(2).join("_").replace(/_/g, " ");
    }

    // First try to find the name from the allProducts array
    const product = this.allProducts.find(
      (p) => p.id.toString() === productId.toString()
    );
    if (product) {
      return product.name;
    }

    if (this.hasCommonProductsTarget) {
      // For existing products, try to find the name from common products DOM
      const commonProductButton = this.commonProductsTarget.querySelector(
        `[data-product-id="${productId}"]`
      );
      if (commonProductButton) {
        return commonProductButton.dataset.productName;
      }
    }

    // If products haven't loaded yet, return a loading message
    if (!this.productsLoaded) {
      return `Loading...`;
    }

    // Fallback
    return `Product ${productId}`;
  }

  updateHiddenField() {
    try {
      const field = this.hasProductIdsFieldTarget
        ? this.productIdsFieldTarget
        : document.querySelector(
            '[data-sector-products-target="productIdsField"]'
          ) ||
          document.querySelector('input[name*="product_ids"]') ||
          document.querySelector("#product_ids");

      if (field) {
        const value = Array.from(this.selectedProductIds).join(",");
        field.value = value;
        // Dispatch a custom event to notify other controllers
        field.dispatchEvent(
          new CustomEvent("productIdsChanged", {
            bubbles: true,
            detail: { value },
          })
        );
      }
    } catch (error) {
      console.error("Error updating hidden field:", error);
    }
  }

  validateForm() {
    // Add a small delay to ensure DOM is updated
    setTimeout(() => {
      // Trigger validation in the main wizard or edit controller
      const wizardController =
        this.application.getControllerForElementAndIdentifier(
          document.querySelector('[data-controller*="facility-wizard"]'),
          "facility-wizard"
        );

      const editController =
        this.application.getControllerForElementAndIdentifier(
          document.querySelector('[data-controller*="facility-edit"]'),
          "facility-edit"
        );

      if (wizardController && wizardController.validateForm) {
        wizardController.validateForm();
      } else if (editController && editController.validateForm) {
        editController.validateForm();
      }
    }, 10);
  }
}
